import { baseApi } from '@/services/api';
import {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  UserResponse,
  UpdateProfileRequest,
  MobileOTPRequest,
  MobileOTPVerify,
  MobileOTPResend,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  FirebaseSocialAuthRequest,
  OTPResponse,
  PasswordResetResponse,
  AuthConfigResponse,
} from './authTypes';

export const authAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ===== BASIC AUTH =====
    
    register: builder.mutation<TokenResponse, RegisterRequest>({
      query: (userData) => ({
        url: '/auth/register',
        method: 'POST',
        data: userData,
      }),
      invalidatesTags: ['Auth'],
    }),

    login: builder.mutation<TokenResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        data: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),

    getCurrentUser: builder.query<UserResponse, void>({
      query: () => ({
        url: '/auth/me',
        method: 'GET',
      }),
      providesTags: ['Auth'],
    }),

    logout: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),

    updateProfile: builder.mutation<UserResponse, UpdateProfileRequest>({
      query: (data) => ({
        url: '/auth/profile',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Auth'],
    }),

    // ===== MOBILE OTP AUTH =====

    requestOTP: builder.mutation<OTPResponse, MobileOTPRequest>({
      query: (data) => ({
        url: '/auth/otp/request',
        method: 'POST',
        data,
      }),
    }),

    verifyOTP: builder.mutation<TokenResponse, MobileOTPVerify>({
      query: (data) => ({
        url: '/auth/otp/verify',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Auth'],
    }),

    resendOTP: builder.mutation<OTPResponse, MobileOTPResend>({
      query: (data) => ({
        url: '/auth/otp/resend',
        method: 'POST',
        data,
      }),
    }),

    // ===== PASSWORD MANAGEMENT =====

    forgotPassword: builder.mutation<PasswordResetResponse, ForgotPasswordRequest>({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        data,
      }),
    }),

    resetPassword: builder.mutation<PasswordResetResponse, ResetPasswordRequest>({
      query: (data) => ({
        url: '/auth/reset-password',
        method: 'POST',
        data,
      }),
    }),

    changePassword: builder.mutation<PasswordResetResponse, ChangePasswordRequest>({
      query: (data) => ({
        url: '/auth/change-password',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Auth'],
    }),

    // ===== SOCIAL AUTH =====

    firebaseSocialAuth: builder.mutation<TokenResponse, FirebaseSocialAuthRequest>({
      query: ({ firebase_token, provider }) => ({
        url: '/auth/firebase/social', // ✅ FIXED: was /auth/social/firebase
        method: 'POST',
        data: { firebase_token, provider },
      }),
      invalidatesTags: ['Auth'],
    }),

    // ===== ADMIN =====

    getUserById: builder.query<UserResponse, string>({
      query: (userId) => ({
        url: `/auth/users/${userId}`,
        method: 'GET',
      }),
      providesTags: ['Auth'],
    }),

    // ===== CONFIG & HEALTH =====

    getAuthConfig: builder.query<AuthConfigResponse, void>({
      query: () => ({
        url: '/auth/config',
        method: 'GET',
      }),
    }),

    getDemoUsers: builder.query<UserResponse[], void>({
      query: () => ({
        url: '/auth/demo-users',
        method: 'GET',
      }),
    }),

    authHealthCheck: builder.query<{ status: string }, void>({
      query: () => ({
        url: '/auth/health',
        method: 'GET',
      }),
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Basic auth
  useRegisterMutation,
  useLoginMutation,
  useGetCurrentUserQuery,
  useLogoutMutation,
  useUpdateProfileMutation,
  
  // OTP auth
  useRequestOTPMutation,
  useVerifyOTPMutation,
  useResendOTPMutation,
  
  // Password management
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  
  // Social auth
  useFirebaseSocialAuthMutation,
  
  // Admin
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  
  // Config & health
  useGetAuthConfigQuery,
  useGetDemoUsersQuery,
  useLazyGetDemoUsersQuery,
  useAuthHealthCheckQuery,
} = authAPI;
